import React, {useContext, useState} from 'react';

import capitalize from 'lodash.capitalize';
import {getCredits, getDate} from 'src/modules/profile/account/tabs/admin-account-api-v2/utils';
import {PRICING_MODEL_LABEL} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/constants';
import {CONTRACT_CELL_WIDTH as WIDTH} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/contracts/components/constants';
import {
    apiV2ContractItem,
    apiV2ContractItemDetails,
    apiV2ContractItemDetailsExpanded,
    apiV2ContractItemExpanded,
    apiV2ContractItemExpandedDetails,
    apiV2ContractItemHandle,
} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/contracts/components/contract-item.module.styl';
import {apiV2ContractTable} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/contracts/components/contract-table.module.styl';
import {getEndDate} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/contracts/utils';
import {PricingModel} from 'src/openapi/admin/models/PricingModel';

import CheckBox from '@pb/kit-component-checkbox';
import SlideBox, {SlideBoxButton} from '@pb/kit-component-slide-box';
import Table, {Body, Cell, Row} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';

import {ContractLimitsController} from '../contract-limits.controller';

import type {ContractsItemDto} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/contracts/typedef';

type Props = {
    data: ContractsItemDto,
    accountId: number,
    disabled?: boolean,
};

export const ContractLimitsItem = ({data, accountId, disabled}: Props) => {
    const [expanded, setExpanded] = useState(false);
    const {locale} = useContext(LocaleContext);

    const {contractInfo, creditBalanceInfo} = data;
    const {contractId, contractNumber, pricingModel, startDate} = contractInfo;
    const endDate = getEndDate(data);

    const packageSize = pricingModel === PricingModel.SUBSCRIPTION
        ? (contractInfo as {packageSize?: string}).packageSize
        : undefined;
    const initialPackage = pricingModel === PricingModel.INTEGRATION
        ? undefined
        : (contractInfo as {initialPackage?: number}).initialPackage;
    const {remainingCredits} = creditBalanceInfo;

    const showEndDate = pricingModel !== PricingModel.PAY_PER_CALL;

    const itemClasses = `${apiV2ContractItem} ${expanded ? apiV2ContractItemExpanded : ''}`;
    const detailsClasses = `${apiV2ContractItemDetails} ${expanded ? apiV2ContractItemDetailsExpanded : ''}`;
    const align = 'middle' as const;

    return (
        <div className={itemClasses}>
            <div className={apiV2ContractItemHandle}>
                <SlideBoxButton onClick={() => setExpanded(state => !state)}>
                    {slideBoxClick => (
                        <CheckBox
                            name="default"
                            onCheckBoxChange={slideBoxClick}
                            checked={expanded}
                            custom={true}
                            usePropsOnly={true}
                        />
                    )}
                </SlideBoxButton>
            </div>

            <div className={detailsClasses}>
                <div className={apiV2ContractTable}>
                    <Table>
                        <Body>
                            <Row>
                                <Cell {...{align, width: WIDTH.contract}}>
                                    {contractNumber}
                                </Cell>
                                <Cell {...{align, width: WIDTH.pricing}}>
                                    {PRICING_MODEL_LABEL[pricingModel]}
                                </Cell>
                                <Cell {...{align, width: WIDTH.size}}>
                                    {packageSize ? capitalize(packageSize) : '-'}
                                </Cell>
                                <Cell {...{className: 'text-align_center', width: WIDTH.start}}>
                                    {getDate(startDate, locale)}
                                </Cell>
                                <Cell {...{className: 'text-align_center', width: WIDTH.end}}>
                                    {showEndDate ? getDate(endDate || null, locale) : '-'}
                                </Cell>
                                <Cell {...{align, className: 'text-align_right', width: WIDTH.package}}>
                                    {getCredits(initialPackage ?? null, locale)}
                                </Cell>
                                <Cell {...{align, className: 'text-align_right'}}>
                                    {getCredits(remainingCredits, locale)}
                                </Cell>
                            </Row>
                        </Body>
                    </Table>
                </div>

                <SlideBox {...{shown: expanded}}>
                    <div className={apiV2ContractItemExpandedDetails}>
                        {expanded && contractId ? (
                            <ContractLimitsController
                                accountId={accountId}
                                contractId={contractId}
                                pricingModel={pricingModel}
                                disabled={disabled}
                            />
                        ) : null}
                    </div>
                </SlideBox>
            </div>
        </div>
    );
};
